import type { ReactNode } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { PAGE_TOOLBAR, filterButtonClass } from './pageToolbarStyles'

interface FilterBarProps {
  children: ReactNode
  className?: string
}

/** Linha de filtros — flex wrap, gap-2, altura 40px nos controles */
export function FilterBar({ children, className }: FilterBarProps) {
  return <div className={cn(PAGE_TOOLBAR.filterRow, className)}>{children}</div>
}

interface FilterSearchProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  className?: string
}

export function FilterSearch({
  value,
  onChange,
  placeholder = 'Buscar...',
  ariaLabel = 'Buscar',
  className,
}: FilterSearchProps) {
  return (
    <div className={cn('relative group min-w-0 flex-1 max-w-2xl', className)}>
      <Search
        size={15}
        className='absolute left-3 top-1/2 -translate-y-1/2 text-text-muted transition-colors group-focus-within:text-accent'
        aria-hidden
      />
      <input
        type='search'
        className={PAGE_TOOLBAR.searchInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
      {value && (
        <button
          type='button'
          className='absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-all hover:bg-red-500/10 hover:text-red-400'
          onClick={() => onChange('')}
          title='Limpar busca'
          aria-label='Limpar busca'
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}

interface FilterSelectProps {
  value: string
  onChange: (value: string) => void
  children: ReactNode
  icon?: ReactNode
  className?: string
  title?: string
  ariaLabel?: string
}

export function FilterSelect({
  value,
  onChange,
  children,
  icon,
  className,
  title,
  ariaLabel,
}: FilterSelectProps) {
  return (
    <label className='flex shrink-0 items-center gap-1.5'>
      {icon}
      <select
        className={cn(PAGE_TOOLBAR.select, 'w-auto min-w-[9.5rem] max-w-[11rem]', className)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={title}
        aria-label={ariaLabel ?? title}
      >
        {children}
      </select>
    </label>
  )
}

interface FilterPanelButtonProps {
  active?: boolean
  badgeCount?: number
  onClick: () => void
  children?: ReactNode
  expanded?: boolean
}

export function FilterPanelButton({
  active = false,
  badgeCount = 0,
  onClick,
  children = 'Filtros',
  expanded,
}: FilterPanelButtonProps) {
  const isHighlighted = active || badgeCount > 0
  return (
    <button
      type='button'
      onClick={onClick}
      className={filterButtonClass(isHighlighted)}
      aria-expanded={expanded}
      aria-haspopup='dialog'
    >
      <SlidersHorizontal size={15} aria-hidden />
      <span>{children}</span>
      {badgeCount > 0 && (
        <span className={PAGE_TOOLBAR.filterBadge}>{badgeCount}</span>
      )}
    </button>
  )
}

export function FilterClearButton({ onClick }: { onClick: () => void }) {
  return (
    <button type='button' onClick={onClick} className={PAGE_TOOLBAR.clearButton} title='Limpar todos os filtros'>
      Limpar
    </button>
  )
}
